/**
 * Which stretches of a patient's execution history have actually been loaded.
 *
 * The patient page loads a 180-day window by default and a clinician can then
 * ask for older ranges, INCLUDING ones that end in the past ("a year ending
 * June 2025"). That makes coverage DISJOINT — an island of loaded history with
 * a hole either side — which a single "how far back have we loaded" number
 * cannot describe.
 *
 * Getting that wrong is not cosmetic. The Plans tab decides whether a treatment
 * plan was ever executed by looking for its results, and an empty result set
 * means "never executed" ONLY if the plan's own date was inside a loaded range.
 * With a single number, a disjoint load either overstates coverage (and wrongly
 * blocks a clinician) or understates it (and never blocks at all).
 *
 * Ranges are stored normalized: sorted, non-overlapping, and merged where they
 * touch. Milliseconds internally; ISO strings at the boundary so the store stays
 * serializable.
 */

export type LoadedRange = { from: string; to: string };

type MsRange = { from: number; to: number };

function toMs(range: LoadedRange): MsRange | undefined {
  const from = new Date(range.from).getTime();
  const to = new Date(range.to).getTime();
  // An unparseable or inverted range is dropped rather than stored: a bad entry
  // would claim coverage we do not have, which is the direction that misleads
  // the Plans gate.
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) {
    return undefined;
  }
  return { from, to };
}

/** Sort, merge overlapping/touching, drop invalid. */
export function normalizeRanges(ranges: readonly LoadedRange[]): LoadedRange[] {
  const parsed = ranges
    .map(toMs)
    .filter((r): r is MsRange => r !== undefined)
    .sort((a, b) => a.from - b.from);

  const merged: MsRange[] = [];
  parsed.forEach((range) => {
    const last = merged[merged.length - 1];
    if (last && range.from <= last.to) {
      last.to = Math.max(last.to, range.to);
      return;
    }
    merged.push({ ...range });
  });

  return merged.map(({ from, to }) => ({
    from: new Date(from).toISOString(),
    to: new Date(to).toISOString(),
  }));
}

export function addRange(ranges: readonly LoadedRange[], range: LoadedRange): LoadedRange[] {
  return normalizeRanges([...ranges, range]);
}

/**
 * Is EVERY point between from and to already loaded?
 *
 * Whole-range, not "overlaps": a partially covered request still needs fetching,
 * and treating overlap as coverage would silently skip the missing part.
 */
export function coversRange(ranges: readonly LoadedRange[], range: LoadedRange): boolean {
  const want = toMs(range);
  if (!want) {
    return false;
  }
  return normalizeRanges(ranges).some((r) => {
    const have = toMs(r);
    return !!have && have.from <= want.from && have.to >= want.to;
  });
}

/**
 * Was this instant inside a loaded range?
 *
 * The Plans gate's question. A missing or unparseable date answers NO — fail
 * safe, in the direction that does not block a clinician.
 */
export function coversInstant(
  ranges: readonly LoadedRange[],
  instant: string | undefined,
): boolean {
  if (!instant) {
    return false;
  }
  const at = new Date(instant).getTime();
  if (Number.isNaN(at)) {
    return false;
  }
  return normalizeRanges(ranges).some((r) => {
    const have = toMs(r);
    return !!have && have.from <= at && have.to >= at;
  });
}
